/** Slug for one name segment: lowercase, spaces → underscores. */
function slugPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

function classLabelSlug(className: string): string {
  const slug = slugPart(className);
  const trimmed = slug.replace(/^(class|grade|std|standard)_/, '');
  return trimmed || slug || 'unknown';
}

function sectionLabelSlug(sectionName: string): string {
  const slug = slugPart(sectionName);
  const trimmed = slug.replace(/^section_/, '');
  return trimmed || slug || 'unknown';
}

/** e.g. `class_10_` or `class_10_section_a_` */
function buildClassSectionPrefix(className?: string, sectionName?: string): string {
  const parts: string[] = [];
  if (className?.trim()) parts.push(`class_${classLabelSlug(className)}`);
  if (sectionName?.trim()) parts.push(`section_${sectionLabelSlug(sectionName)}`);
  return parts.length ? `${parts.join('_')}_` : '';
}

export function splitStudentName(fullName: string): { first: string; last: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: 'student', last: 'unknown' };
  if (parts.length === 1) return { first: parts[0], last: parts[0] };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Build a download filename such as:
 * `class_10_section_a_1NPS2026105_sara_nair_1st_term_admit_card.pdf`
 * `class_10_1NPS2026105_sara_nair_1st_term_report_card.pdf`
 */
export function buildStudentDocumentPdfFilename(params: {
  admissionNo: string;
  /** Full name ("Sara Nair") or pass firstName + lastName */
  studentName?: string;
  firstName?: string;
  lastName?: string;
  term?: string;
  documentKind: string;
  /** Prepended for class/section bulk downloads */
  className?: string;
  sectionName?: string;
}): string {
  const admission = params.admissionNo.trim().replace(/\s+/g, '') || 'student';

  let first = params.firstName ?? '';
  let last = params.lastName ?? '';
  if (!first && !last && params.studentName) {
    const split = splitStudentName(params.studentName);
    first = split.first;
    last = split.last;
  }
  const firstSlug = slugPart(first) || 'student';
  const lastSlug = slugPart(last) || firstSlug;

  const termSlug = slugPart(params.term ?? '') || 'term';
  const kindSlug = slugPart(params.documentKind.replace(/-/g, '_')) || 'document';

  const prefix = buildClassSectionPrefix(params.className, params.sectionName);

  return `${prefix}${admission}_${firstSlug}_${lastSlug}_${termSlug}_${kindSlug}.pdf`;
}

export function buildBulkDocumentZipFilename(params: {
  documentKind: string;
  className?: string;
  sectionName?: string;
  term?: string;
  academicYear?: string;
}): string {
  const parts: string[] = [];
  if (params.className?.trim()) parts.push(`class_${classLabelSlug(params.className)}`);
  if (params.sectionName?.trim()) parts.push(`section_${sectionLabelSlug(params.sectionName)}`);
  parts.push(slugPart(params.documentKind.replace(/-/g, '_')) || 'documents');
  if (params.term?.trim()) parts.push(slugPart(params.term));
  if (params.academicYear?.trim()) parts.push(slugPart(params.academicYear));
  return `${parts.join('_')}.zip`;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
